import { Link, Navigate, useParams } from "react-router-dom";
import "./notice.style.scss";
import { notices, noticeLanguages } from "./notice.data";

import { Helmet } from "react-helmet-async";
import MetaThumbnail from "../Blog/imgs/Blogs_01.png";

const metaData = {
  title: "Notice - Moxey.ai",
  thumbnail: MetaThumbnail,
  base: "https://moxey.ai",
  keywords:
    "moxey notice, moxey announcement, moxey security advisory, fuel payment in uae, fleet payment in uae, digital payment for mobility, fleet card, prepaid card",
  description:
    "Official notices and announcements from MoXey for customers and partners, available in English, Arabic, Urdu and Hindi.",
};

const EMAIL_PATTERN = /([\w.+-]+@[\w-]+\.[\w.]+\w)/;

// turns email addresses inside a sentence into mailto links
const linkEmails = (text) =>
  text.split(EMAIL_PATTERN).map((part, index) =>
    EMAIL_PATTERN.test(part) ? (
      <a
        key={index}
        href={`mailto:${part}`}
        className="notice-email-inline"
        dir="ltr"
      >
        {part}
      </a>
    ) : (
      part
    )
  );

function NoticePage() {
  const { lang } = useParams();

  // unknown or missing language falls back to English
  if (!notices[lang]) return <Navigate to="/notice/en" replace />;

  const notice = notices[lang];

  return (
    <div className="moxey-notice">
      <Helmet>
        <title>{metaData.title}</title>
        <meta name="title" content={metaData.title} />
        <meta name="keywords" content={metaData.keywords} />
        <meta name="description" content={metaData.description} />

        <meta property="og:site_name" content="MoXey - Beyond Payments" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaData.title} />
        <meta property="og:description" content={metaData.description} />
        <meta property="og:url" content={`${metaData.base}/notice/${lang}`} />
        <meta
          property="og:image"
          content={`${metaData.base}${metaData.thumbnail}`}
        />

        <meta name="twitter:site" content="@moxey.ai" />
        <meta name="twitter:title" content={metaData.title} />
        <meta name="twitter:description" content={metaData.description} />
        <meta
          name="twitter:image:src"
          content={`${metaData.base}${metaData.thumbnail}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`${metaData.base}/notice/${lang}`} />
      </Helmet>

      {/* notice header */}
      <div className="moxey-notice-header text-center" data-aos="fade-in">
        <h1 className="mb-4 fw-bold container">Notices</h1>
        <h5 className="fw-light container">
          Important announcements for our customers and partners
        </h5>
      </div>

      {/* notice content */}
      <div className="container w-100">
        <div
          className="moxey-notice-content shadow"
          data-aos="fade-up"
          data-aos-delay="350"
        >
          <div className="moxey-notice-content-header">
            <div className="filters w-100">
              {noticeLanguages.map((language) => (
                <Link
                  key={language.code}
                  to={`/notice/${language.code}`}
                  className={`btn rounded-pill px-4 btn-sm fw-bold me-3 ${
                    language.code === lang
                      ? "btn-success"
                      : "btn-outline-success"
                  }`}
                  aria-current={language.code === lang ? "page" : undefined}
                >
                  {language.nativeName}
                </Link>
              ))}
            </div>
          </div>

          <article
            className={`notice-body notice-${lang}`}
            dir={notice.dir}
            lang={lang}
          >
            <span className="notice-badge">{notice.badge}</span>
            <h2 className="notice-title fw-bold">{notice.title}</h2>

            <div className="notice-meta">
              <span className="notice-meta-label">{notice.dateLabel}</span>
              <span className="notice-meta-value">{notice.date}</span>
            </div>

            <hr className="notice-divider" />

            <p className="fw-bold">{notice.greeting}</p>
            {notice.paragraphs.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}

            <div className="notice-highlight">
              {linkEmails(notice.contact)}
              {notice.contactEmails &&
                notice.contactEmails.map((email) => (
                  <a
                    key={email}
                    href={`mailto:${email}`}
                    className="notice-email"
                    dir="ltr"
                  >
                    {email}
                  </a>
                ))}
            </div>

            <div className="notice-signature">
              <p className="mb-1">{notice.signOff}</p>
              <p className="mb-0 text-success fw-bold">{notice.company}</p>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

export default NoticePage;
